import React, { useState } from 'react';
import { usePortfolio } from '../context/PortfolioContext';
import './TransactionHistory.css';

const TransactionHistory: React.FC = () => {
  const { state } = usePortfolio();
  const { portfolio } = state;
  const [filter, setFilter] = useState<'all' | 'buy' | 'sell'>('all');

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDateTime = (date: Date) => {
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Sort transactions by timestamp (newest first)
  const sortedTransactions = [...portfolio.transactions].sort(
    (a, b) => b.timestamp.getTime() - a.timestamp.getTime()
  );

  // Filter transactions based on selected filter
  const filteredTransactions = filter === 'all' 
    ? sortedTransactions 
    : sortedTransactions.filter(t => t.type === filter);

  // Group transactions by date
  const groupedTransactions = filteredTransactions.reduce((groups, transaction) => {
    const date = transaction.timestamp.toDateString();
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(transaction);
    return groups;
  }, {} as Record<string, typeof filteredTransactions>);

  const getTransactionIcon = (type: 'buy' | 'sell') => {
    return type === 'buy' ? '📈' : '📉';
  };

  const getTransactionColor = (type: 'buy' | 'sell') => {
    return type === 'buy' ? 'buy' : 'sell';
  };

  if (portfolio.transactions.length === 0) {
    return (
      <div className="transaction-history">
        <div className="history-header">
          <h3>Transaction History</h3>
          <span className="history-subtitle">Your trading activity</span>
        </div>
        <div className="empty-state">
          <div className="empty-icon">💹</div>
          <p>No transactions yet</p>
          <small>Start trading to see your transaction history</small>
        </div>
      </div>
    );
  }

  return (
    <div className="transaction-history">
      <div className="history-header">
        <h3>Transaction History</h3>
        <div className="header-controls">
          <span className="history-subtitle">{portfolio.transactions.length} transaction{portfolio.transactions.length !== 1 ? 's' : ''}</span>
          <div className="filter-buttons">
            <button
              className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All
            </button>
            <button
              className={`filter-btn ${filter === 'buy' ? 'active' : ''}`}
              onClick={() => setFilter('buy')}
            >
              Buys
            </button>
            <button
              className={`filter-btn ${filter === 'sell' ? 'active' : ''}`}
              onClick={() => setFilter('sell')}
            >
              Sells
            </button>
          </div>
        </div>
      </div>

      <div className="transactions-list">
        {Object.entries(groupedTransactions).map(([date, transactions]) => (
          <div key={date} className="transaction-group">
            <div className="group-header">
              <span className="group-date">{formatDate(new Date(date))}</span>
              <span className="group-count">{transactions.length} transaction{transactions.length !== 1 ? 's' : ''}</span>
            </div>
            
            <div className="group-transactions">
              {transactions.map((transaction) => (
                <div key={transaction.id} className="transaction-item">
                  <div className="transaction-icon">
                    <span className={`icon ${getTransactionColor(transaction.type)}`}>
                      {getTransactionIcon(transaction.type)}
                    </span>
                  </div>
                  
                  <div className="transaction-details">
                    <div className="transaction-main">
                      <span className={`transaction-type ${getTransactionColor(transaction.type)}`}>
                        {transaction.type === 'buy' ? 'Bought' : 'Sold'}
                      </span>
                      <span className="transaction-symbol">{transaction.ticker}</span>
                    </div>
                    <div className="transaction-meta">
                      <span className="transaction-time">{formatDateTime(transaction.timestamp)}</span>
                    </div>
                  </div>
                  
                  <div className="transaction-quantity">
                    <span className="quantity-value">{transaction.quantity.toLocaleString()}</span>
                    <span className="quantity-label">shares</span>
                  </div>
                  
                  <div className="transaction-price">
                    <span className="price-value">{formatCurrency(transaction.price)}</span>
                    <span className="price-label">per share</span>
                  </div>
                  
                  <div className="transaction-total">
                    <span className="total-value">{formatCurrency(transaction.total)}</span>
                    <span className="total-label">total</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {filteredTransactions.length === 0 && (
        <div className="no-results">
          <div className="no-results-icon">🔍</div>
          <p>No {filter} transactions found</p>
          <small>Try adjusting your filter or make some trades</small>
        </div>
      )}
    </div>
  );
};

export default TransactionHistory;
